import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getMe, getToken, logoutRequest, mapApiRole, roleCanEdit } from '../api/auth';
import { createBoard, getBoardFull, listBoards, patchBoard } from '../api/boards';
import { setOnSaveSuccess } from '../api/client';
import { indexProjects, mapBoardFullToSheet } from '../api/mapBoard';
import { createProject, moveProject as moveProjectApi, patchProject, projectFieldToApi } from '../api/projects';
import { patchSection } from '../api/sections';
import { createSegment, deleteSegment, patchSegment } from '../api/segments';
import { createSection, createTeam, listCanonicalTeams } from '../api/teams';
import stageColorsConfig from '../config/stage-colors.json';
import { displayPriority, displayWorkType, normalizeWorkType } from '../utils/catalogDisplay';
import { CHIP_DEFS, DEFAULT_STAGES, META_COLS, WRAP_CELL_MAX_H, WRAP_META_COL_KEYS, ZOOM_OPTS, buildTimeline, computeBoardWindow, currentWeekIndex, dateToWeekIndex, gridBg, isoDate, passesFilter, prStyle, segsFor, titleParts, typeStyle, weekLabel, zoomCfg, } from '../utils/planHelpers';
import { SECTION_COLOR_DEFAULT, sectionPalette, sectionStyle } from '../utils/sectionHelpers';
export function usePlanApp(defaultRole = 'editor', defaultZoom = 'compact') {
    const byIdRef = useRef({});
    const paintingRef = useRef(null);
    const dragRef = useRef(null);
    const dragIdRef = useRef(null);
    const undoStackRef = useRef([]);
    const saveTimerRef = useRef();
    const toastTimerRef = useRef();
    const sessionCheckedRef = useRef(false);
    const cellPopCloserRef = useRef(null);
    const sheetLoadGenRef = useRef(0);
    const [userRole, setUserRole] = useState(defaultRole);
    const [stages, setStages] = useState(() => ({ ...DEFAULT_STAGES }));
    const [state, setState] = useState(() => ({
        screen: 'login',
        showPw: false,
        loginLoading: false,
        loginError: '',
        userEmail: '',
        userFirstName: '',
        userLastName: '',
        userJobTitle: '',
        userFullName: '',
        passwordUnset: false,
        activeTab: 0,
        search: '',
        chipSet: {},
        zoomUser: null,
        userMenuOpen: false,
        newSheetOpen: false,
        collapsed: {},
        editing: null,
        menu: null,
        selectedId: null,
        dropId: null,
        selSeg: null,
        popup: null,
        sectionPopup: null,
        cellPopup: null,
        stageSel: 'cursor',
        saveState: 'saved',
        toast: null,
        sheets: [],
        boardIds: [],
        boardsLoading: false,
        sheetLoading: false,
        loadError: '',
        paintTick: 0,
    }));
    const stateRef = useRef(state);
    stateRef.current = state;
    useEffect(() => {
        if (stageColorsConfig?.stages) {
            setStages((prev) => ({ ...prev, ...stageColorsConfig.stages }));
        }
    }, []);
    useEffect(() => {
        return () => {
            if (cellPopCloserRef.current) {
                document.removeEventListener('mousedown', cellPopCloserRef.current, true);
                cellPopCloserRef.current = null;
            }
        };
    }, []);
    const isEditor = roleCanEdit(userRole);
    const isAdmin = userRole === 'admin';
    const zoomKey = state.zoomUser ?? defaultZoom;
    const z = zoomCfg(zoomKey);
    const weekW = z.w;
    const rowH = z.h;
    const metaW = META_COLS.reduce((a, c) => a + c.w, 0);
    const markDirty = useCallback(() => {
        if (saveTimerRef.current)
            clearTimeout(saveTimerRef.current);
        setState((s) => ({ ...s, saveState: 'saving' }));
        saveTimerRef.current = setTimeout(() => setState((s) => ({ ...s, saveState: 'saved' })), 800);
    }, []);
    const showToast = useCallback((t) => {
        if (toastTimerRef.current)
            clearTimeout(toastTimerRef.current);
        setState((s) => ({ ...s, toast: { text: t, showCheck: false } }));
        toastTimerRef.current = setTimeout(() => setState((s) => ({ ...s, toast: null })), 1800);
    }, []);
    const showSavedToast = useCallback(() => {
        if (toastTimerRef.current)
            clearTimeout(toastTimerRef.current);
        setState((s) => ({ ...s, toast: { text: 'Сохранено', showCheck: true } }));
        toastTimerRef.current = setTimeout(() => setState((s) => ({ ...s, toast: null })), 1800);
    }, []);
    useEffect(() => {
        setOnSaveSuccess(showSavedToast);
        return () => setOnSaveSuccess(null);
    }, [showSavedToast]);
    const persistSegment = useCallback(async (seg) => {
        if (!seg.segId)
            return;
        try {
            await patchSegment(seg.segId, {
                stage: seg.stage,
                start: seg.start,
                len: seg.len,
                label: seg.label ?? null,
            });
        }
        catch {
            showToast('Ошибка сохранения сегмента');
        }
    }, [showToast]);
    const persistProjectSegments = useCallback(async (p) => {
        await Promise.all(p.segs.filter((s) => s.segId).map((s) => persistSegment(s)));
    }, [persistSegment]);
    const loadBoardFull = useCallback(async (boardId, tabIdx) => {
        const gen = ++sheetLoadGenRef.current;
        setState((s) => ({ ...s, sheetLoading: true }));
        try {
            const full = await getBoardFull(boardId);
            let sheet = mapBoardFullToSheet(full);
            const expected = computeBoardWindow(full.year, full.quarter);
            if (full.weeksCount < expected.weeksCount) {
                await patchBoard(boardId, {
                    weekStart: expected.weekStart,
                    weeksCount: expected.weeksCount,
                });
                sheet = {
                    ...sheet,
                    weekStart: expected.weekStart,
                    weeksCount: expected.weeksCount,
                };
            }
            if (gen !== sheetLoadGenRef.current)
                return;
            setState((s) => {
                const sheets = [...s.sheets];
                sheets[tabIdx] = sheet;
                if (s.activeTab === tabIdx) {
                    byIdRef.current = {};
                    indexProjects(sheet, byIdRef.current);
                }
                return { ...s, sheets, sheetLoading: false, saveState: 'saved' };
            });
        }
        catch {
            if (gen !== sheetLoadGenRef.current)
                return;
            setState((s) => ({ ...s, sheetLoading: false }));
            showToast('Ошибка загрузки листа');
        }
    }, [showToast]);
    const loadBoards = useCallback(async (preferTab = 0) => {
        setState((s) => ({ ...s, boardsLoading: true, loadError: '' }));
        try {
            const items = await listBoards();
            const boardIds = items.map((b) => b.id);
            const sheets = items.map((b) => ({
                boardId: b.id,
                title: b.title,
                weekStart: b.weekStart,
                weeksCount: b.weeksCount,
                year: b.year,
                quarter: b.quarter,
                teams: [],
            }));
            const activeTab = boardIds.length ? Math.min(preferTab, boardIds.length - 1) : 0;
            setState((s) => ({
                ...s,
                boardIds,
                sheets,
                activeTab,
                boardsLoading: false,
                sheetLoading: boardIds.length > 0,
            }));
            if (boardIds.length > 0) {
                await loadBoardFull(boardIds[activeTab], activeTab);
            }
        }
        catch {
            setState((s) => ({
                ...s,
                boardsLoading: false,
                loadError: 'Не удалось загрузить листы',
            }));
            showToast('Не удалось загрузить листы');
        }
    }, [loadBoardFull, showToast]);
    useEffect(() => {
        if (sessionCheckedRef.current)
            return;
        sessionCheckedRef.current = true;
        let cancelled = false;
        (async () => {
            try {
                const user = await getMe();
                if (cancelled)
                    return;
                setUserRole(mapApiRole(user.role));
                setState((s) => ({
                    ...s,
                    userEmail: user.email,
                    userFirstName: user.firstName ?? '',
                    userLastName: user.lastName ?? '',
                    userJobTitle: user.jobTitle ?? '',
                    userFullName: user.fullName ?? user.name ?? '',
                    passwordUnset: !!user.passwordUnset,
                    screen: 'plan',
                }));
                await loadBoards(0);
            }
            catch {
                /* no session — stay on login */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [loadBoards]);
    const pushUndo = useCallback(() => {
        try {
            undoStackRef.current.push(JSON.stringify(stateRef.current.sheets));
            if (undoStackRef.current.length > 50)
                undoStackRef.current.shift();
        }
        catch {
            /* ignore serialization errors */
        }
    }, []);
    const undo = useCallback(() => {
        if (!undoStackRef.current.length) {
            showToast('Нечего отменять');
            return;
        }
        const snap = JSON.parse(undoStackRef.current.pop());
        byIdRef.current = {};
        snap.forEach((sh) => sh.teams.forEach((t) => t.sections.forEach((sec) => sec.projects.forEach((p) => {
            byIdRef.current[p.id] = p;
        }))));
        setState((s) => ({
            ...s,
            sheets: snap,
            selSeg: null,
            popup: null,
            editing: null,
            menu: null,
            activeTab: Math.min(s.activeTab, snap.length - 1),
            paintTick: s.paintTick + 1,
        }));
        markDirty();
        showToast('Действие отменено');
    }, [markDirty, showToast]);
    const bumpPaint = useCallback(() => {
        setState((s) => ({ ...s, paintTick: s.paintTick + 1 }));
    }, []);
    const weekFromEvent = useCallback((e, w) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const i = Math.floor((e.clientX - rect.left) / w);
        return Math.max(0, Math.min(weeksCountRef.current - 1, i));
    }, []);
    const popupDelete = useCallback(() => {
        setState((s) => {
            const ss = s.selSeg;
            if (!ss)
                return s;
            const p = byIdRef.current[ss.id];
            const seg = p?.segs[ss.idx];
            if (seg) {
                pushUndo();
                const segId = seg.segId;
                p.segs.splice(ss.idx, 1);
                markDirty();
                if (segId) {
                    deleteSegment(segId).catch(() => showToast('Ошибка удаления сегмента'));
                }
            }
            return { ...s, selSeg: null, popup: null };
        });
    }, [markDirty, pushUndo, showToast]);
    useEffect(() => {
        const onUp = () => {
            const pt = paintingRef.current;
            if (pt?.creating) {
                paintingRef.current = null;
                const p = byIdRef.current[pt.id];
                if (p && pt.moved) {
                    pushUndo();
                    const a = Math.min(pt.from, pt.to);
                    const b = Math.max(pt.from, pt.to);
                    const newSeg = {
                        stage: stateRef.current.stageSel,
                        start: a,
                        len: b - a + 1,
                    };
                    p.segs.push(newSeg);
                    setState((s) => ({
                        ...s,
                        selectedId: pt.id,
                        paintTick: s.paintTick + 1,
                    }));
                    markDirty();
                    createSegment(Number(p.id), {
                        stage: newSeg.stage,
                        start: newSeg.start,
                        len: newSeg.len,
                    })
                        .then((created) => {
                        newSeg.segId = created.id;
                    })
                        .catch(() => showToast('Ошибка сохранения сегмента'));
                }
                else {
                    bumpPaint();
                }
            }
            else {
                paintingRef.current = null;
            }
            if (dragRef.current) {
                const g = dragRef.current;
                const p = byIdRef.current[g.id];
                if (p && g.snapPushed) {
                    if (g.type === 'move') {
                        persistProjectSegments(p);
                    }
                    else {
                        const seg = p.segs[g.idx];
                        if (seg)
                            persistSegment(seg);
                    }
                }
                dragRef.current = null;
                document.body.style.cursor = '';
            }
        };
        const onMove = (e) => {
            const g = dragRef.current;
            if (!g)
                return;
            const p = byIdRef.current[g.id];
            if (!p)
                return;
            const dw = Math.round((e.clientX - g.x) / weekW);
            let changed = false;
            if (g.type === 'move') {
                const minS = Math.min(...g.orig);
                const maxE = Math.max(...g.orig.map((s0, i) => s0 + p.segs[i].len));
                const d = Math.max(-minS, Math.min(dw, weeksCountRef.current - maxE));
                p.segs.forEach((s, i) => {
                    const ns = g.orig[i] + d;
                    if (s.start !== ns) {
                        s.start = ns;
                        changed = true;
                    }
                });
            }
            else {
                const s = p.segs[g.idx];
                if (!s)
                    return;
                if (g.type === 'resizeR') {
                    const nl = Math.max(1, Math.min(g.len + dw, weeksCountRef.current - g.start));
                    if (nl !== s.len) {
                        s.len = nl;
                        changed = true;
                    }
                }
                else {
                    const ns = Math.max(0, Math.min(g.start + dw, g.start + g.len - 1));
                    if (ns !== s.start) {
                        s.start = ns;
                        s.len = g.len + (g.start - ns);
                        changed = true;
                    }
                }
            }
            if (changed) {
                if (g.snap && !g.snapPushed) {
                    undoStackRef.current.push(g.snap);
                    if (undoStackRef.current.length > 50)
                        undoStackRef.current.shift();
                    g.snapPushed = true;
                }
                markDirty();
                bumpPaint();
            }
        };
        const onKey = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.code === 'KeyZ' && !e.shiftKey) {
                const t = e.target;
                if (t && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA'))
                    return;
                if (!isEditor || stateRef.current.screen !== 'plan')
                    return;
                e.preventDefault();
                undo();
                return;
            }
            if ((e.key === 'Delete' || e.key === 'Backspace') && stateRef.current.selSeg) {
                const t = e.target;
                if (t && (t.tagName === 'INPUT' || t.tagName === 'SELECT' || t.tagName === 'TEXTAREA'))
                    return;
                e.preventDefault();
                popupDelete();
            }
        };
        window.addEventListener('mouseup', onUp);
        window.addEventListener('mousemove', onMove);
        window.addEventListener('keydown', onKey);
        return () => {
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('keydown', onKey);
        };
    }, [bumpPaint, isEditor, markDirty, persistProjectSegments, persistSegment, popupDelete, pushUndo, showToast, undo, weekW]);
    useEffect(() => {
        const onDown = (e) => {
            const target = e.target;
            if (target?.closest?.('[data-menu-keep]') || target?.closest?.('[data-seg-keep]'))
                return;
            setState((s) => {
                let next = s;
                if (s.menu || s.userMenuOpen)
                    next = { ...next, menu: null, userMenuOpen: false };
                if (s.selSeg || s.popup)
                    next = { ...next, selSeg: null, popup: null };
                if (s.sectionPopup)
                    next = { ...next, sectionPopup: null };
                return next === s ? s : next;
            });
        };
        document.addEventListener('mousedown', onDown, true);
        return () => document.removeEventListener('mousedown', onDown, true);
    }, []);
    useEffect(() => () => {
        if (saveTimerRef.current)
            clearTimeout(saveTimerRef.current);
        if (toastTimerRef.current)
            clearTimeout(toastTimerRef.current);
    }, []);
    const sheet = state.sheets[state.activeTab] || state.sheets[0];
    const sheetEmpty = !state.sheetLoading && !state.boardsLoading && (!sheet || sheet.teams.length === 0);
    const timeline = useMemo(() => {
        if (sheet?.weekStart && sheet?.weeksCount) {
            return buildTimeline(sheet.weekStart, sheet.weeksCount);
        }
        if (sheet?.year && sheet?.quarter) {
            const win = computeBoardWindow(sheet.year, sheet.quarter);
            return buildTimeline(win.weekStart, win.weeksCount);
        }
        const win = computeBoardWindow(2026, 3);
        return buildTimeline(win.weekStart, win.weeksCount);
    }, [sheet?.weekStart, sheet?.weeksCount, sheet?.year, sheet?.quarter]);
    const { weeks, months: monthDefs, weekDates } = timeline;
    const weeksCount = weeks.length;
    const timelineW = weeksCount * weekW;
    const totalW = 280 + 100 + 90 + metaW + timelineW;
    const curWeekIdx = useMemo(() => currentWeekIndex(weekDates), [weekDates]);
    const weeksCountRef = useRef(weeksCount);
    weeksCountRef.current = weeksCount;
    const weekDatesRef = useRef(weekDates);
    weekDatesRef.current = weekDates;
    const weeksRef = useRef(weeks);
    weeksRef.current = weeks;
    const paintingPreview = useMemo(() => {
        const pt = paintingRef.current;
        if (!pt?.creating)
            return null;
        return { id: pt.id, from: pt.from, to: pt.to, stageSel: state.stageSel };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.paintTick, state.stageSel]);
    const rows = useMemo(() => {
        const result = [];
        if (!sheet)
            return result;
        sheet.teams.forEach((team, teamIdx) => {
            const collapsed = !!state.collapsed[team.name];
            const sections = team.sections.map((sec) => ({
                sec,
                projects: sec.projects.filter((p) => passesFilter(p, state.search, state.chipSet)),
            }));
            const total = sections.reduce((a, s) => a + s.projects.length, 0);
            result.push({
                kind: 'team',
                isTeam: true,
                isSection: false,
                isProject: false,
                name: team.name,
                chev: collapsed ? '▸' : '▾',
                count: `· ${total}`,
            });
            if (collapsed)
                return;
            sections.forEach(({ sec, projects }, secIdx) => {
                if (!projects.length)
                    return;
                if (sec.name) {
                    const ss = sectionStyle(sec.color);
                    result.push({
                        kind: 'section',
                        isTeam: false,
                        isSection: true,
                        isProject: false,
                        name: sec.name,
                        sectionTeamIdx: teamIdx,
                        sectionSecIdx: secIdx,
                        sectionBg: ss.bg,
                        sectionTextColor: ss.text,
                        sectionBorderColor: ss.border,
                    });
                }
                projects.forEach((p) => {
                    const sel = state.selectedId === p.id;
                    const edT = state.editing?.id === p.id && state.editing.field === 'title';
                    const ts = typeStyle(normalizeWorkType(p.type) || '');
                    const ps = prStyle(p.priority);
                    const cells = META_COLS.map((col) => {
                        const raw = String(p[col.key] || '');
                        const editing = state.editing?.id === p.id && state.editing.field === col.key;
                        const orange = col.key === 'effort' || col.key === 'duration' || col.key === 'deps' || col.key === 'ab';
                        const wrap = WRAP_META_COL_KEYS.includes(col.key);
                        return {
                            key: col.key,
                            w: col.w,
                            just: col.just,
                            text: raw,
                            wrap,
                            maxH: wrap ? WRAP_CELL_MAX_H : undefined,
                            linkMax: Math.max(80, col.w - 40),
                            bg: col.key === 'effect' && /^\d/.test(raw) ? '#FEF9C3' : 'transparent',
                            color: orange ? '#C2680C' : col.key === 'effectComment' || col.key === 'comment' ? '#475569' : '#0F172A',
                            fs: orange ? 'italic' : 'normal',
                            editing: !!editing,
                            show: !editing,
                        };
                    });
                    const sr = segsFor(p, weekW, stages, {
                        selSeg: state.selSeg,
                        isEditor,
                        painting: paintingPreview,
                        weeks,
                    });
                    let gMin = Infinity;
                    let gMax = 0;
                    (p.segs || []).forEach((s0) => {
                        gMin = Math.min(gMin, s0.start);
                        gMax = Math.max(gMax, s0.start + s0.len);
                    });
                    const hasSegs = gMax > 0 && gMin !== Infinity;
                    result.push({
                        kind: 'project',
                        isTeam: false,
                        isSection: false,
                        isProject: true,
                        id: p.id,
                        title: p.title,
                        titleParts: titleParts(p.title),
                        titleEditing: !!edT,
                        titleShow: !edT,
                        type: displayWorkType(p.type),
                        typeBg: ts.bg,
                        typeColor: ts.color,
                        typeBd: ts.bd,
                        typeMenu: !!(state.menu?.id === p.id && state.menu.field === 'type'),
                        pr: displayPriority(p.priority),
                        prBg: ps.bg,
                        prColor: ps.color,
                        prMenu: !!(state.menu?.id === p.id && state.menu.field === 'priority'),
                        bg: sel ? '#EFF6FF' : '#FFFFFF',
                        selBorder: sel ? '3px solid #2563EB' : '3px solid transparent',
                        dropBorder: state.dropId === p.id ? '2px solid #2563EB' : '2px solid transparent',
                        cells,
                        segs: sr.segs,
                        tlMinH: Math.max(rowH, 5 + sr.maxLane * 20 + 22 + 5),
                        groupSelected: sel && isEditor && hasSegs && !(state.selSeg && state.selSeg.id === p.id),
                        grabLeft: hasSegs ? Math.max(0, gMin * weekW - 22) : 0,
                        grabTop: 4,
                        groupBoxL: hasSegs ? gMin * weekW - 1 : 0,
                        groupBoxW: hasSegs ? (gMax - gMin) * weekW + 2 : 0,
                    });
                });
            });
        });
        return result;
    }, [sheet, state, weekW, stages, isEditor, rowH, paintingPreview, weeks]);
    const palette = useMemo(() => {
        const order = stageColorsConfig.order ||
            ['estimate', 'design', 'research', 'analytics', 'dev', 'qa', 'businessTest', 'release'];
        const items = [
            {
                key: 'cursor',
                label: 'Курсор (выделение)',
                bg: '#FFFFFF',
                txt: '➤',
                ring: state.stageSel === 'cursor' ? '0 0 0 2px #FFFFFF, 0 0 0 4px #334155' : 'none',
            },
            ...order.map((k) => ({
                key: k,
                label: stages[k]?.label ?? k,
                bg: stages[k]?.color ?? '#94A3B8',
                ring: state.stageSel === k ? '0 0 0 2px #FFFFFF, 0 0 0 4px #334155' : 'none',
            })),
        ];
        items.push({
            key: 'erase',
            label: 'Ластик',
            bg: 'linear-gradient(135deg, #FFFFFF 44%, #DC2626 44%, #DC2626 56%, #FFFFFF 56%)',
            ring: state.stageSel === 'erase' ? '0 0 0 2px #FFFFFF, 0 0 0 4px #334155' : 'none',
        });
        return items;
    }, [stages, state.stageSel]);
    const popupData = useMemo(() => {
        if (!state.popup || !state.selSeg)
            return null;
        const p = byIdRef.current[state.selSeg.id];
        const sg = p?.segs[state.selSeg.idx];
        if (!sg)
            return null;
        const cfg = stages[sg.stage] || { label: sg.stage, color: '#94A3B8' };
        const displayName = sg.label || (sg.stage === 'run' ? p.runLabel || 'RUN' : cfg.label);
        return {
            x: state.popup.x,
            y: state.popup.y,
            stage: displayName,
            stageName: displayName,
            defaultStageName: cfg.label || sg.stage,
            swatch: cfg.color || '#94A3B8',
            popupDate: isoDate(weekDates[sg.start]),
            popupMin: isoDate(weekDates[0]),
            popupMax: isoDate(weekDates[weekDates.length - 1]),
            len: sg.len,
            range: `${weekLabel(sg.start, weeks)} – ${weekLabel(sg.start + sg.len - 1, weeks)}`,
        };
    }, [state.popup, state.selSeg, stages, weekDates, weeks]);
    const sectionPopupData = useMemo(() => {
        if (!state.sectionPopup || !sheet)
            return null;
        const { teamIdx, secIdx, x, y } = state.sectionPopup;
        const sec = sheet.teams[teamIdx]?.sections[secIdx];
        if (!sec)
            return null;
        return {
            x,
            y,
            name: sec.name || '',
            color: sec.color || SECTION_COLOR_DEFAULT,
            palette: sectionPalette(),
        };
    }, [state.sectionPopup, sheet]);
    const cellPopupData = useMemo(() => {
        if (!state.cellPopup)
            return null;
        const p = byIdRef.current[state.cellPopup.id];
        const col = META_COLS.find((c) => c.key === state.cellPopup.field);
        if (!p || !col)
            return null;
        const text = String(p[col.key] || '');
        return {
            x: state.cellPopup.x,
            y: state.cellPopup.y,
            title: col.label,
            text,
        };
    }, [state.cellPopup]);
    const ddMenuData = useMemo(() => {
        const menu = state.menu;
        if (!menu || (menu.field !== 'type' && menu.field !== 'priority'))
            return null;
        const p = byIdRef.current[menu.id];
        if (!p)
            return null;
        const isType = menu.field === 'type';
        const curVal = isType ? displayWorkType(p.type) : displayPriority(p.priority);
        return {
            x: menu.x,
            y: menu.y,
            rowId: menu.id,
            field: menu.field,
            currentValue: curVal,
        };
    }, [state.menu]);
    const findProjectSection = useCallback((projectId) => {
        const sheet = stateRef.current.sheets[stateRef.current.activeTab];
        if (!sheet)
            return null;
        for (const team of sheet.teams) {
            for (const sec of team.sections) {
                if (sec.projects.some((p) => p.id === projectId)) {
                    return { sec, team };
                }
            }
        }
        return null;
    }, []);
    const moveProject = useCallback((srcId, dstId) => {
        if (!srcId || srcId === dstId)
            return;
        pushUndo();
        let dstSectionId;
        setState((s) => {
            const sheets = [...s.sheets];
            const activeSheet = sheets[s.activeTab];
            if (!activeSheet)
                return s;
            let src;
            activeSheet.teams.forEach((t) => t.sections.forEach((sec) => {
                const i = sec.projects.findIndex((p) => p.id === srcId);
                if (i >= 0)
                    src = { sec, i };
            }));
            if (!src)
                return s;
            const proj = src.sec.projects[src.i];
            src.sec.projects.splice(src.i, 1);
            let placed = false;
            activeSheet.teams.forEach((t) => t.sections.forEach((sec) => {
                if (placed)
                    return;
                const j = sec.projects.findIndex((p) => p.id === dstId);
                if (j >= 0) {
                    sec.projects.splice(j, 0, proj);
                    proj.sectionId = sec.id;
                    dstSectionId = sec.id;
                    placed = true;
                }
            }));
            if (!placed)
                src.sec.projects.splice(src.i, 0, proj);
            return { ...s, sheets };
        });
        markDirty();
        const srcSec = findProjectSection(srcId)?.sec;
        if (dstSectionId && srcSec?.id && dstSectionId !== srcSec.id) {
            moveProjectApi(Number(srcId), dstSectionId).catch(() => showToast('Ошибка перемещения'));
        }
    }, [findProjectSection, markDirty, pushUndo, showToast]);
    const handlers = {
        onLoginSuccess: (user) => {
            setUserRole(mapApiRole(user.role));
            setState((s) => ({
                ...s,
                userEmail: user.email,
                userFirstName: user.firstName ?? '',
                userLastName: user.lastName ?? '',
                userJobTitle: user.jobTitle ?? '',
                userFullName: user.fullName ?? user.name ?? '',
                passwordUnset: !!user.passwordUnset,
                screen: 'plan',
                loginError: '',
            }));
            loadBoards(0);
        },
        openProfile: () => setState((s) => ({ ...s, screen: 'profile', userMenuOpen: false })),
        openAccess: () => setState((s) => ({ ...s, screen: 'access', userMenuOpen: false })),
        openAbout: () => setState((s) => ({ ...s, screen: 'about', userMenuOpen: false })),
        openDesignSystem: () => setState((s) => ({ ...s, screen: 'design-system', userMenuOpen: false })),
        closeProfile: () => setState((s) => ({ ...s, screen: 'plan' })),
        goHome: () => {
            setState((s) => ({
                ...s,
                screen: getToken() ? 'plan' : 'login',
            }));
        },
        updateUserProfile: (user) => setState((s) => ({
            ...s,
            userFirstName: user.firstName ?? s.userFirstName,
            userLastName: user.lastName ?? s.userLastName,
            userJobTitle: user.jobTitle ?? s.userJobTitle,
            userFullName: user.fullName ?? s.userFullName,
        })),
        setPasswordUnset: (passwordUnset) => setState((s) => ({ ...s, passwordUnset })),
        setScreen: (screen) => setState((s) => ({ ...s, screen })),
        setLoginLoading: (loginLoading) => setState((s) => ({ ...s, loginLoading })),
        setLoginError: (loginError) => setState((s) => ({ ...s, loginError })),
        setUserEmail: (userEmail) => setState((s) => ({ ...s, userEmail })),
        togglePw: () => setState((s) => ({ ...s, showPw: !s.showPw })),
        logout: () => {
            logoutRequest().finally(() => {
                byIdRef.current = {};
                undoStackRef.current = [];
                setState((s) => ({
                    ...s,
                    screen: 'login',
                    userMenuOpen: false,
                    loginLoading: false,
                    loginError: '',
                    userEmail: '',
                    userFirstName: '',
                    userLastName: '',
                    userJobTitle: '',
                    userFullName: '',
                    passwordUnset: false,
                    sheets: [],
                    boardIds: [],
                    activeTab: 0,
                    selectedId: null,
                    editing: null,
                }));
            });
        },
        toggleUserMenu: () => setState((s) => ({ ...s, userMenuOpen: !s.userMenuOpen })),
        switchTab: (idx) => {
            setState((s) => ({
                ...s,
                activeTab: idx,
                selectedId: null,
                editing: null,
                menu: null,
                selSeg: null,
                popup: null,
                cellPopup: null,
            }));
            const boardId = stateRef.current.boardIds[idx];
            const sheet = stateRef.current.sheets[idx];
            if (sheet?.teams?.length) {
                byIdRef.current = {};
                indexProjects(sheet, byIdRef.current);
            }
            else if (boardId) {
                loadBoardFull(boardId, idx);
            }
        },
        openNewSheet: () => setState((s) => ({ ...s, newSheetOpen: true })),
        closeNewSheet: () => setState((s) => ({ ...s, newSheetOpen: false })),
        createSheet: (tribe, q, y) => {
            const title = `Трайб ${tribe} Q${q}'${String(y).slice(2)}`;
            const year = Number(y);
            const quarter = Number(q);
            const { weekStart, weeksCount } = computeBoardWindow(year, quarter);
            markDirty();
            createBoard({
                tribe,
                year,
                quarter,
                title,
                weekStart,
                weeksCount,
            })
                .then((board) => {
                setState((s) => ({ ...s, newSheetOpen: false }));
                const idx = stateRef.current.boardIds.length;
                loadBoards(idx).then(() => {
                    const tabIdx = stateRef.current.boardIds.indexOf(board.id);
                    if (tabIdx >= 0) {
                        setState((s) => ({ ...s, activeTab: tabIdx }));
                        loadBoardFull(board.id, tabIdx);
                    }
                });
            })
                .catch(() => showToast('Не удалось создать лист'));
        },
        addTeam: () => {
            const boardId = stateRef.current.boardIds[stateRef.current.activeTab];
            const sheet = stateRef.current.sheets[stateRef.current.activeTab];
            if (!boardId || !sheet)
                return;
            const tribe = sheet.title.match(/Трайб\s+(\S+)/)?.[1] ?? 'I';
            markDirty();
            listCanonicalTeams(tribe)
                .then(async (teams) => {
                const ct = teams[0];
                if (!ct)
                    throw new Error('no_teams');
                const team = await createTeam(boardId, ct.id);
                const section = await createSection(team.id, 'DEVELOPMENT ↓', 'projects');
                const project = await createProject(section.id, {
                    title: 'Новый проект',
                    type: 'OKR-C',
                    priority: 'P1',
                });
                await loadBoardFull(boardId, stateRef.current.activeTab);
                setState((s) => ({ ...s, selectedId: String(project.id) }));
            })
                .catch(() => showToast('Не удалось добавить команду'));
        },
        setSearch: (search) => setState((s) => ({ ...s, search })),
        toggleChip: (key) => setState((s) => ({ ...s, chipSet: { ...s.chipSet, [key]: !s.chipSet[key] } })),
        setZoom: (zoomUser) => setState((s) => ({ ...s, zoomUser })),
        toggleTeam: (name) => setState((s) => ({ ...s, collapsed: { ...s.collapsed, [name]: !s.collapsed[name] } })),
        selectRow: (id) => setState((s) => ({
            ...s,
            selectedId: id,
            selSeg: s.selSeg && s.selSeg.id === id ? s.selSeg : null,
            popup: s.selSeg && s.selSeg.id === id ? s.popup : null,
        })),
        cellClick: (e) => {
            const ct = e.currentTarget;
            const id = ct.getAttribute('data-row');
            const field = ct.getAttribute('data-field');
            const span = ct.querySelector('span[data-cell-content]');
            if (!id || !field || !span)
                return;
            const els = [span, ...Array.from(span.querySelectorAll('a'))];
            const clipped = els.some((el) => el.scrollWidth > el.clientWidth + 1 || el.scrollHeight > el.clientHeight + 1);
            if (!clipped) {
                setState((s) => (s.cellPopup ? { ...s, cellPopup: null } : s));
                return;
            }
            const r = ct.getBoundingClientRect();
            const w = 420;
            const h = 348;
            let x = Math.max(8, Math.min(r.left, window.innerWidth - w - 16));
            let y = r.bottom + 6;
            if (y + h > window.innerHeight - 8)
                y = Math.max(8, r.top - h - 6);
            if (!cellPopCloserRef.current) {
                const onDown = (ev) => {
                    const t = ev.target;
                    if (t?.closest?.('[data-cellpop-keep]'))
                        return;
                    setState((s) => (s.cellPopup ? { ...s, cellPopup: null } : s));
                };
                cellPopCloserRef.current = onDown;
                document.addEventListener('mousedown', onDown, true);
            }
            setState((s) => ({ ...s, cellPopup: { id, field, x, y } }));
        },
        closeCellPopup: () => setState((s) => ({ ...s, cellPopup: null })),
        startEdit: (id, field) => {
            if (!isEditor) {
                showToast('Недостаточно прав — режим просмотра');
                return;
            }
            setState((s) => ({ ...s, editing: { id, field }, menu: null, cellPopup: null }));
        },
        commitCell: (id, field, value) => {
            const p = byIdRef.current[id];
            if (p) {
                const prev = String(p[field] ?? '');
                if (prev !== value) {
                    pushUndo();
                    p[field] = value;
                    markDirty();
                    patchProject(Number(id), projectFieldToApi(field, value)).catch(() => showToast('Ошибка сохранения'));
                }
            }
            setState((s) => ({ ...s, editing: null }));
        },
        cancelEdit: () => setState((s) => ({ ...s, editing: null })),
        openMenu: (id, field, e) => {
            if (!isEditor) {
                showToast('Недостаточно прав — режим просмотра');
                return;
            }
            e.stopPropagation();
            const cur = stateRef.current.menu;
            if (cur?.id === id && cur.field === field) {
                setState((s) => ({ ...s, menu: null }));
                return;
            }
            const r = e.currentTarget.getBoundingClientRect();
            const menuH = field === 'priority' ? 175 : 130;
            let y = r.bottom + 4;
            if (y + menuH > window.innerHeight - 8)
                y = Math.max(8, r.top - menuH - 4);
            const x = Math.max(8, Math.min(r.left + 4, window.innerWidth - 140));
            setState((s) => ({ ...s, menu: { id, field, x, y } }));
        },
        pickOption: (id, field, value) => {
            const p = byIdRef.current[id];
            if (p) {
                pushUndo();
                const v = value === '—' ? '' : field === 'type' ? normalizeWorkType(value) : value;
                p[field] = v;
                markDirty();
                patchProject(Number(id), projectFieldToApi(field, v)).catch(() => showToast('Ошибка сохранения'));
            }
            setState((s) => ({ ...s, menu: null }));
        },
        pickStage: (stageSel) => setState((s) => ({ ...s, stageSel })),
        dragStart: (id) => {
            dragIdRef.current = id;
        },
        dragOver: (id) => {
            if (!dragIdRef.current)
                return;
            setState((s) => (s.dropId !== id ? { ...s, dropId: id } : s));
        },
        dropRow: (dstId) => {
            moveProject(dragIdRef.current, dstId);
            dragIdRef.current = null;
            setState((s) => ({ ...s, dropId: null }));
        },
        dragEnd: () => {
            dragIdRef.current = null;
            setState((s) => (s.dropId ? { ...s, dropId: null } : s));
        },
        paintDown: (id, e) => {
            if (!isEditor)
                return;
            if (e.target !== e.currentTarget)
                return;
            if (state.stageSel === 'erase' || state.stageSel === 'cursor')
                return;
            e.preventDefault();
            const idx = weekFromEvent(e, weekW);
            paintingRef.current = { id, creating: true, from: idx, to: idx, moved: false };
            bumpPaint();
        },
        paintMove: (id, e) => {
            const pt = paintingRef.current;
            if (!pt?.creating || pt.id !== id)
                return;
            pt.moved = true;
            const idx = weekFromEvent(e, weekW);
            if (idx !== pt.to) {
                pt.to = idx;
                bumpPaint();
            }
        },
        segDown: (id, segIdx, e) => {
            e.stopPropagation();
            if (!isEditor)
                return;
            if (state.stageSel === 'erase' && segIdx !== -1) {
                const p = byIdRef.current[id];
                const seg = p?.segs[segIdx];
                if (seg) {
                    pushUndo();
                    const segId = seg.segId;
                    p.segs.splice(segIdx, 1);
                    markDirty();
                    if (segId) {
                        deleteSegment(segId).catch(() => showToast('Ошибка удаления сегмента'));
                    }
                    setState((s) => ({ ...s, selSeg: null, popup: null }));
                }
            }
        },
        segClick: (id, segIdx, e) => {
            e.stopPropagation();
            if (segIdx === -1)
                return;
            setState((s) => {
                const ss = s.selSeg;
                if (ss && ss.id === id) {
                    return { ...s, selectedId: id, selSeg: { id, idx: segIdx } };
                }
                return { ...s, selectedId: id, selSeg: null, popup: null };
            });
        },
        segDbl: (id, segIdx, e) => {
            e.stopPropagation();
            if (!isEditor || segIdx === -1)
                return;
            setState((s) => ({
                ...s,
                selectedId: id,
                selSeg: { id, idx: segIdx },
                popup: {
                    x: Math.min(e.clientX + 12, window.innerWidth - 268),
                    y: Math.min(e.clientY + 10, window.innerHeight - 270),
                },
            }));
        },
        resizeDown: (id, segIdx, side, e) => {
            e.stopPropagation();
            e.preventDefault();
            const p = byIdRef.current[id];
            const s = p?.segs[segIdx];
            if (!s)
                return;
            dragRef.current = {
                type: side === 'l' ? 'resizeL' : 'resizeR',
                id,
                idx: segIdx,
                x: e.clientX,
                start: s.start,
                len: s.len,
                snap: JSON.stringify(state.sheets),
            };
        },
        groupDown: (id, e) => {
            e.stopPropagation();
            e.preventDefault();
            const p = byIdRef.current[id];
            if (!p?.segs.length)
                return;
            dragRef.current = {
                type: 'move',
                id,
                x: e.clientX,
                orig: p.segs.map((s) => s.start),
                snap: JSON.stringify(state.sheets),
            };
            document.body.style.cursor = 'grabbing';
        },
        popupClose: () => setState((s) => ({ ...s, popup: null })),
        openSectionPopup: (teamIdx, secIdx, e) => {
            if (!isEditor)
                return;
            e.stopPropagation();
            setState((s) => ({
                ...s,
                sectionPopup: { teamIdx, secIdx, x: e.clientX, y: e.clientY },
                menu: null,
                popup: null,
                selSeg: null,
            }));
        },
        sectionPopupClose: () => setState((s) => ({ ...s, sectionPopup: null })),
        sectionSetName: (name) => {
            const sp = stateRef.current.sectionPopup;
            if (!sp)
                return;
            const sec = stateRef.current.sheets[stateRef.current.activeTab]?.teams[sp.teamIdx]?.sections[sp.secIdx];
            if (!sec)
                return;
            const sectionId = sec.id;
            if (!sectionId) {
                showToast('Ошибка: секция без id');
                return;
            }
            pushUndo();
            const v = name.trim();
            setState((s) => {
                const sheets = [...s.sheets];
                const target = sheets[s.activeTab]?.teams[sp.teamIdx]?.sections[sp.secIdx];
                if (!target)
                    return s;
                target.name = v || null;
                return { ...s, sheets };
            });
            markDirty();
            patchSection(sectionId, { name: v || null }).catch(() => showToast('Ошибка сохранения секции'));
        },
        sectionSetColor: (color) => {
            const sp = stateRef.current.sectionPopup;
            if (!sp)
                return;
            const sec = stateRef.current.sheets[stateRef.current.activeTab]?.teams[sp.teamIdx]?.sections[sp.secIdx];
            if (!sec || sec.color === color)
                return;
            const sectionId = sec.id;
            if (!sectionId) {
                showToast('Ошибка: секция без id');
                return;
            }
            pushUndo();
            setState((s) => {
                const sheets = [...s.sheets];
                const target = sheets[s.activeTab]?.teams[sp.teamIdx]?.sections[sp.secIdx];
                if (!target)
                    return s;
                target.color = color;
                return { ...s, sheets };
            });
            markDirty();
            patchSection(sectionId, { color }).catch(() => showToast('Ошибка сохранения секции'));
        },
        popupDelete,
        popupSetStart: (isoDateStr) => {
            const ss = state.selSeg;
            if (!ss || !isoDateStr)
                return;
            const p = byIdRef.current[ss.id];
            const s = p?.segs[ss.idx];
            if (!s)
                return;
            pushUndo();
            const idx = dateToWeekIndex(isoDateStr, weekDatesRef.current);
            s.start = idx;
            s.len = Math.max(1, Math.min(s.len, weeksCountRef.current - s.start));
            markDirty();
            bumpPaint();
            persistSegment(s);
        },
        popupSetLen: (len) => {
            const ss = state.selSeg;
            if (!ss)
                return;
            const p = byIdRef.current[ss.id];
            const s = p?.segs[ss.idx];
            if (!s)
                return;
            pushUndo();
            s.len = Math.max(1, Math.min(Math.round(len) || 1, weeksCountRef.current - s.start));
            markDirty();
            bumpPaint();
            persistSegment(s);
        },
        popupSetName: (name) => {
            const ss = state.selSeg;
            if (!ss)
                return;
            const p = byIdRef.current[ss.id];
            const s = p?.segs[ss.idx];
            if (!s)
                return;
            const cfg = stages[s.stage] || { label: s.stage };
            const v = name.trim();
            const cur = s.label || '';
            const nv = v === cfg.label ? '' : v;
            if (nv === cur)
                return;
            pushUndo();
            if (nv)
                s.label = nv;
            else
                delete s.label;
            markDirty();
            bumpPaint();
            persistSegment(s);
        },
    };
    return {
        state,
        handlers,
        isEditor,
        isAdmin,
        userRole,
        zoomKey,
        weekW,
        rowH,
        timelineW,
        totalW,
        sheet,
        sheetEmpty,
        sheetLoading: state.sheetLoading,
        rows,
        palette,
        stages,
        ddMenuData,
        popupData,
        sectionPopupData,
        cellPopupData,
        gridBackground: gridBg(weekW),
        noResults: !sheetEmpty && rows.filter((r) => r.isProject).length === 0,
        metaCols: META_COLS,
        months: monthDefs.map((m) => ({ name: m.name, w: m.span * weekW })),
        weeks: weeks.map((w, i) => ({
            d: String(w.d),
            bg: curWeekIdx >= 0 && i === curWeekIdx ? '#DBEAFE' : 'transparent',
            color: curWeekIdx >= 0 && i === curWeekIdx ? '#1D4ED8' : '#64748B',
            fw: curWeekIdx >= 0 && i === curWeekIdx ? '700' : '500',
        })),
        chips: CHIP_DEFS.map((c) => {
            const on = !!state.chipSet[c.key];
            return {
                key: c.key,
                label: c.label,
                bg: on ? '#EFF6FF' : '#FFFFFF',
                color: on ? '#1D4ED8' : '#475569',
                bd: on ? '#93C5FD' : '#E2E8F0',
            };
        }),
        zoomOpts: ZOOM_OPTS.map(([key, label]) => ({
            key: key,
            label,
            bg: zoomKey === key ? '#EFF6FF' : '#FFFFFF',
            color: zoomKey === key ? '#1D4ED8' : '#475569',
        })),
        tabs: state.sheets.map((s, i) => ({
            title: s.title,
            idx: i,
            bg: i === state.activeTab ? '#FFFFFF' : '#E2E8F0',
            color: i === state.activeTab ? '#1D4ED8' : '#475569',
            fw: i === state.activeTab ? '700' : '500',
            bd: i === state.activeTab ? '#E2E8F0' : 'transparent',
        })),
        stageLabel: state.stageSel === 'erase'
            ? 'Ластик'
            : state.stageSel === 'cursor'
                ? 'Курсор'
                : stages[state.stageSel]?.label || '',
        paintCursor: isEditor ? (state.stageSel === 'cursor' ? 'default' : 'crosshair') : 'default',
        showPalette: isEditor && state.screen === 'plan' && !sheetEmpty,
    };
}
